import {List, Map} from 'immutable';
import {getInstanceIndex} from '@packages/systems/core/utils/NativeIdUtils';
import {weakMemo} from '@packages/utilities/memo';
import {ExpansionProps} from '@packages/systems/render';
import {convertRepeaterSlugPath} from '@packages/systems/dynamo/utils/SlugUtils';
import {getIn} from '@packages/utilities/imm4-backports';
import {
  isDynamoItem,
  isDynamoList,
  isDynamoWrapper,
} from '@packages/utilities/elementTypeUtils';
import {
  getBoundRepeaterFieldIdPathFromElement,
  getCollectionIdFromElement,
  getCollectionListTemporaryUiState,
  isDynBoundElement,
} from '@packages/systems/dynamo/utils/DynamoElementUtils';
import {getChildren} from '@packages/utilities/platform';
import {ExpressionElement} from '@packages/systems/wfdl-ast';
import {PluginsContext} from '@packages/systems/wfdl';

const SAMPLE_ITEMS = List(['sample-id']);
const DYNAMO_ITEM_IDS_PATH = ['Dynamo', 'listItemIds'];

export const transformDynamoPropsForExport = <T extends ExpansionProps>(
  props: T
): T => {
  const {element, pluginsContext} = props;
  if (isDynamoList(element)) {
    return {
      ...props,
      // Setting one sample item to dynamo context so that DynamoList
      // and DynamoItem will be rendered in addition to DynamoEmpty.
      pluginsContext: pluginsContext.setIn(DYNAMO_ITEM_IDS_PATH, SAMPLE_ITEMS),
    };
  }

  return props;
};

export const transformDynamoPropsForDesigner = <T extends ExpansionProps>(
  props: T,
  itemNamesById: {[itemId: string]: string}
): T => {
  const {element} = props;

  if (isDynamoWrapper(element)) {
    return renderExpandedDynamoWrapper(props);
  }

  if (isDynamoItem(element)) {
    return renderExpandedDynamoItem(props, itemNamesById);
  }

  if (isEmptyDynBoundHtmlElement(props)) {
    return renderWithoutDynamicStyles(props);
  }

  return props;
};

const getListItemLabel = (
  dynamoContext: Map<string, any>,
  environmentContext: Map<string, any>,
  itemNamesById: {[itemId: string]: string}
) => {
  const cachedItems = dynamoContext.get('cachedItems');
  const collectionId = dynamoContext.get('collectionId');
  const itemId = dynamoContext.get('itemId');
  const itemIndex = getInstanceIndex(environmentContext) || 0;
  if (dynamoContext.get('repeaterSlugPath')) {
    const fileName =
      itemId &&
      getIn(
        cachedItems,
        [
          itemId,
          ...convertRepeaterSlugPath(dynamoContext.get('repeaterSlugPath', [])),
          'file',
          'origFileName',
        ],
        ''
      );
    return fileName ? fileName : `Image ${itemIndex + 1}`;
  } else {
    const collectionName = dynamoContext.getIn([
      'collections',
      collectionId,
      'pluralName',
    ]);
    const itemName = itemNamesById[itemId];
    return collectionName && itemName
      ? `${collectionName}: ${itemName}`
      : `Collection Item ${itemIndex + 1}`;
  }
};

const updateWithDynamoContent = weakMemo((element: ExpressionElement) =>
  weakMemo((pluginsContext: PluginsContext) => {
    const isCollectionBound = getCollectionIdFromElement(element) !== undefined;
    const isRepeaterBound =
      getBoundRepeaterFieldIdPathFromElement(element).length > 0;
    const dynamoContext = pluginsContext.get('Dynamo');
    const tempUiState = getCollectionListTemporaryUiState(element);
    const elementHasEmptyState = tempUiState === 'empty';
    const emptyState =
      isCollectionBound &&
      (elementHasEmptyState ||
        (dynamoContext.get('listItemIds', List()).size === 0 && !tempUiState));
    const emptyRepeaterState = isRepeaterBound && elementHasEmptyState;

    const updatedDynamoContext = dynamoContext
      .update('listItemIds', (listItemIds) => {
        if (emptyState) {
          return List();
        } else if (isCollectionBound) {
          return listItemIds;
        } else if (isRepeaterBound) {
          return List();
        } else {
          return List(['sample-1', 'sample-2', 'sample-3']);
        }
      })
      .update('repeaterItemsCount', (originalValue) => {
        if (emptyRepeaterState) {
          return 0;
        }
        return originalValue;
      });

    return pluginsContext.set('Dynamo', updatedDynamoContext);
  })
);

const renderExpandedDynamoWrapper = <T extends ExpansionProps>(props: T): T => {
  const {element, classes, pluginsContext} = props;

  const dynamoContext = pluginsContext.get('Dynamo', Map());
  const repeaterSlugPath = dynamoContext.get('repeaterSlugPath');
  const repeaterItemCount = dynamoContext.get('repeaterItemsCount', 0);

  const isBound =
    getCollectionIdFromElement(element) !== undefined ||
    getBoundRepeaterFieldIdPathFromElement(element).length > 0;
  const tempUiState = getCollectionListTemporaryUiState(element);
  const emptyState =
    isBound &&
    (tempUiState === 'empty' ||
      ((repeaterSlugPath && repeaterSlugPath.length > 0
        ? repeaterItemCount === 0
        : dynamoContext.get('listItemIds', List()).size === 0) &&
        !tempUiState));

  const updatedClasses = classes.update((wrapperClasses) => {
    if (isBound) {
      const emptyCollectionItemState =
        !dynamoContext.get('loading') &&
        (repeaterSlugPath && repeaterSlugPath.length > 0
          ? repeaterItemCount === 0
          : dynamoContext.get('listItemIds', List()).size === 0) &&
        !emptyState;
      // Add class for showing "This Collection List does not contain any items" message.
      // When the message is shown both DynamoList and DynamoEmpty will render `null`.
      return emptyCollectionItemState
        ? wrapperClasses.push('wf-no-items')
        : wrapperClasses;
    } else {
      return wrapperClasses.push('wf-unbound');
    }
  });

  return {
    ...props,
    classes: updatedClasses,
    pluginsContext: updateWithDynamoContent(element)(pluginsContext),
  };
};

const renderExpandedDynamoItem = <T extends ExpansionProps>(
  props: T,
  itemNamesById: {[itemId: string]: string}
): T => {
  const {attributes, pluginsContext, environmentContext} = props;

  const dynamoContext = pluginsContext.get('Dynamo', Map());
  const updateAttributes = attributes.set(
    'data-dyn-label',
    getListItemLabel(
      dynamoContext as Map<string, any>,
      environmentContext,
      itemNamesById
    )
  );

  return {
    ...props,
    attributes: updateAttributes,
  };
};

const isEmptyDynBoundHtmlElement = <T extends ExpansionProps>(
  props: T
): boolean => {
  const {element, innerHTML, pluginsContext} = props;

  if (isDynBoundElement(element, 'innerHTML')) {
    const loading = pluginsContext.getIn(['Dynamo', 'loading']);
    if (!loading && !innerHTML) {
      const children = getChildren(element);
      return Boolean(!children || children.length === 0);
    }
  }

  return false;
};

const renderWithoutDynamicStyles = <T extends ExpansionProps>(props: T) => {
  return {...props, style: {}};
};
